import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, DestroyRef, QueryList, ViewChildren, computed, effect, inject, input, signal } from '@angular/core';
import { FormGroup, ReactiveFormsModule } from '@angular/forms';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ButtonModule } from 'primeng/button';
import { TooltipModule } from 'primeng/tooltip';
import { CardModule } from 'primeng/card';
import { OneToManyComponent } from '../one-to-many/one-to-many.component';
import { PaginationMobileVersionComponent } from '../pagination-mobile-version/pagination-mobile-version.component';
import { UtcToLocalPipe } from '../../pipes/utc-to-local.pipe';
import { RelativeDateLabelPipe } from '../../pipes/relative-date-label.pipe';
import { FileUploadService } from '../file-upload/file-upload.service';
import { FileUploadComponent } from '../file-upload/file-upload.component';
import { catchError, finalize, of } from 'rxjs';
import { ConfirmationService, MessageService } from 'primeng/api';
import { ConfirmDialogModule } from 'primeng/confirmdialog';
import { ErrorHandlingService, TokenService } from '../../../core';

@Component({
  selector: 'app-documents-card',
  imports: [
    CommonModule,
    ReactiveFormsModule,
    ButtonModule,
    TooltipModule,
    CardModule,
    ConfirmDialogModule,
    OneToManyComponent,
    PaginationMobileVersionComponent,
    UtcToLocalPipe,
    RelativeDateLabelPipe,
  ],
  providers: [ConfirmationService],
  templateUrl: './documents-card.component.html',
  styleUrls: ['./documents-card.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class DocumentsCardComponent {
  fileUploadService = inject(FileUploadService);
  private destroyRef = inject(DestroyRef);
  private errorHandlingService = inject(ErrorHandlingService);
  private confirmationService = inject(ConfirmationService);
  private messageService = inject(MessageService);
  private tokenService = inject(TokenService);

  formGroup = input.required<FormGroup>();
  parentIdKey = input<string>('quoteID');
  foreignKeyName = input<string>('quoteID');
  defaultSort = input<any>({ documentName: 'ASC' });
  cols = input<any[]>([]);
  fields = input<string[]>([]);
  enablePagination = input<boolean>(false);
  pageSize = input<number>(5);
  rowsPerPageOptions = input<number[]>([5, 10, 20]);
  showHeader = input<boolean>(true);

  documentsList = signal<any[]>([]);
  officialDateDocuments = signal<Set<string>>(new Set());
  documentsSkip = signal<number>(0);
  documentsTake = signal<number>(5);
  deletingDocumentId = signal<string | null>(null);

  fileUploadComponentType = FileUploadComponent;

  paginatedDocuments = computed(() => {
    const list = this.documentsList();
    if (!this.enablePagination() || list.length <= this.pageSize()) {
      return list;
    }
    const skip = this.documentsSkip();
    const take = this.documentsTake();
    return list.slice(skip, skip + take);
  });

  constructor() {
    effect(() => {
      const take = this.pageSize();
      this.documentsTake.set(take);
    });
  }

  @ViewChildren(OneToManyComponent) oneToManyComponents!: QueryList<OneToManyComponent>;

  uploadDocument() {
    const documentComp = this.oneToManyComponents?.find((c) => c.id === 'documentID-hidden');

    if (documentComp) {
      documentComp.show();

      setTimeout(() => {
        const visibleDocComp = this.oneToManyComponents?.find((c) => c.id === 'documentID');
        if (visibleDocComp) {
          setTimeout(() => {
            visibleDocComp.load({ first: 0, rows: visibleDocComp.paginateQuery.take });
          }, 1000);
        }
      }, 500);
    }
  }

  /** Opens the same file-upload modal as Upload; save still uses `Document/Upload` with `DocumentID` in the form (blob). */
  editDocument(doc: any) {
    if (!doc) {
      return;
    }
    const documentComp = this.oneToManyComponents?.find((c) => c.id === 'documentID-hidden');
    if (documentComp) {
      documentComp.edit({ ...doc }, 0);
    }
  }

  onDocumentCardClick(doc: any): void {
    if (this.deletingDocumentId()) {
      return;
    }
    this.editDocument(doc);
  }

  canDeleteDocument(): boolean {
    return this.tokenService.hasPermission(this.fileUploadService.endPoint + 'Delete');
  }

  confirmDeleteDocument(event: Event, doc: any): void {
    if (!this.canDeleteDocument() || this.deletingDocumentId()) {
      return;
    }
    const id = this.resolveDocumentId(doc);
    if (!id) {
      return;
    }
    this.confirmationService.confirm({
      target: event.target as EventTarget,
      message: 'Do you want to delete this document?',
      header: 'Delete Confirmation',
      icon: 'pi pi-info-circle',
      acceptButtonStyleClass: 'quote-confirm-outline-danger',
      rejectButtonStyleClass: 'quote-confirm-text-primary',
      accept: () => this.deleteDocument(doc),
      reject: () => {},
    });
  }

  deleteDocument(doc: any): void {
    const id = this.resolveDocumentId(doc);
    if (!id) {
      return;
    }
    this.deletingDocumentId.set(id);
    this.fileUploadService
      .deleteFile(id)
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        finalize(() => this.deletingDocumentId.set(null)),
        catchError((error) => {
          return this.errorHandlingService.handleError(error, 'Error deleting document');
        }),
      )
      .subscribe(() => {
        this.messageService.add({
          severity: 'success',
          summary: 'Success',
          detail: 'Document deleted',
        });
        this.reloadDocumentsLists();
      });
  }

  isDeletingDocument(doc: any): boolean {
    const id = this.resolveDocumentId(doc);
    return id != null && this.deletingDocumentId() === id;
  }

  private resolveDocumentId(doc: any): string | null {
    const raw = doc?.documentID ?? doc?.documentId ?? doc?.DocumentID;
    if (raw == null || raw === '') {
      return null;
    }
    return String(raw).trim() || null;
  }

  private reloadDocumentsLists(): void {
    const hidden = this.oneToManyComponents?.find((c) => c.id === 'documentID-hidden');
    if (hidden) {
      hidden.load({ first: 0, rows: hidden.paginateQuery.take });
    }
    const visible = this.oneToManyComponents?.find((c) => c.id === 'documentID');
    if (visible) {
      visible.load({ first: 0, rows: visible.paginateQuery.take });
    }
  }

  onDataDocument(event: any) {
    if (!event || !event.list) {
      this.documentsList.set([]);
      return;
    }

    for (const item of event.list) {
      if (item.documentName) {
        item.documentName = this.cleanDocumentName(item.documentName);
      }

      if (item.typeDescription) {
        item.type = item.typeDescription;
      } else {
        item.type = 'Unknown Type';
      }
    }

    this.documentsList.set(event.list || []);
    this.documentsSkip.set(0);
    this.officialDateDocuments.set(new Set());

    const form = this.formGroup();
    const control = form?.get('documents');
    if (control) {
      control.patchValue(event.list);
    }
  }

  downloadDocument(doc: any) {
    if (!doc) return;

    this.fileUploadService
      .getDownloadRequest(doc)
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        catchError((error) => {
          this.errorHandlingService.handleError(error, 'Error downloading file');
          return of(new Blob());
        }),
      )
      .subscribe((response) => {
        const blob = response instanceof Blob ? response : new Blob([response as BlobPart], { type: doc.documentType || 'application/octet-stream' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = doc.documentName || 'document';
        a.click();
        URL.revokeObjectURL(url);
      });
  }

  onDocumentsPageChange(event: { first: number; rows: number }): void {
    this.documentsSkip.set(event.first);
    this.documentsTake.set(event.rows);
    this.officialDateDocuments.set(new Set());
  }

  getDocumentKey(doc: any): string {
    return String(doc?.documentID || `${doc?.createdOn || ''}-${doc?.documentName || ''}`);
  }

  toggleDocumentDateDisplay(doc: any, event?: Event): void {
    event?.stopPropagation();
    const key = this.getDocumentKey(doc);
    if (!key) return;
    const next = new Set(this.officialDateDocuments());
    if (next.has(key)) {
      next.delete(key);
    } else {
      next.add(key);
    }
    this.officialDateDocuments.set(next);
  }

  isOfficialDocumentDate(doc: any): boolean {
    return this.officialDateDocuments().has(this.getDocumentKey(doc));
  }

  private cleanDocumentName(name: string): string {
    const normalized = name.replace(/\\/g, '/');
    const fileName = normalized.split('/').filter(Boolean).pop() ?? normalized;
    const match = fileName.match(/^\d+_(.*)/);
    return match ? match[1] : fileName;
  }
}
